import get from 'lodash/get'
import requestAnimationFrame from 'raf'
import random from 'lodash/random'

// How many on screen at a time.
const LIMIT = 16

// Physics shit
const FRICTION = 0.95
const GRAVITY = 0.025

// Velocity ranges
const VELOCITY_RANGE_X = [-6.8, 6.8]
const VELOCITY_RANGE_Y = [-6.4, -4.8]

// Particle size range
const SIZE_RANGE = [3, 8]

// How wide should the angle be
const PARTICLE_ANGLE = 0.8
const ROTATION_SPEED_RANGE = [0.02, 0.17]

// Fading
const FADE_IN_SPEED = 0.18
const FADE_OUT_SPEED = 0.04

// At what point should we start fading out (max 1.0)
// Higher = start fading closer to the bottom
// Lower = start fading early
const FADE_Y_START = 0.45

// What colors to use.
const COLORS = ['#000000', '#007BE7']

// Device pixel ratio, need to scale the canvas so the
// shapes don't like like rubbish on Retina displays.
const DPR = window.devicePixelRatio || 1

export interface Confetti {
  configure(props?: ConfettiProps): void
  start(delay?: number): void
  stop(): void
}

/**
 * Confetti props.
 */
export interface ConfettiProps {
  limit?: number
  friction?: number
  gravity?: number
  viewportHeight?: number
  viewportWidth?: number
  velocityRangeX?: [number, number]
  velocityRangeY?: [number, number]
  sizeRange?: [number, number]
  confettiSource?: Partial<ConfettiSource>
}

/**
 * Confetti source.
 */
export interface ConfettiSource {
  width: number
  height: number
  x: number
  y: number
}

/**
 * Creates a confetti renderer.
 *
 * @param {*} canvas
 */
export function createConfetti(canvas: HTMLCanvasElement): Confetti {
  // Drawing context.
  const ctx = canvas.getContext('2d')!

  // Particle generator.
  const generator = createParticleGenerator()

  // If using delayed start, this is the timer ID.
  let delayedStartTimer: number | null = null

  // Tracks whether the animation is running.
  // If not, no animation frames are requested.
  let running = false

  // Track the canvas width and height so we don't need to access it every time.
  let canvasWidth = 0
  let canvasHeight = 0

  // Configures defaults.
  configure()

  return {
    configure,
    start,
    stop,
  }

  /**
   * Configures the particle generator.
   * @param {*} props
   */
  function configure(props = {}) {
    // Account for High DPI displays (Retina)
    const rect = canvas.getBoundingClientRect()
    canvasWidth = rect.width * DPR
    canvasHeight = rect.height * DPR
    canvas.width = canvasWidth
    canvas.height = canvasHeight
    ctx.scale(DPR, DPR)
    generator.configure({
      ...props,
      viewportWidth: rect.width,
      viewportHeight: rect.height,
    })
  }

  /**
   * Starts the animation.
   *
   * @param {number | undefined} delay
   */
  function start(delay?: number) {
    running = true
    if (delay) {
      delayedStartTimer = window.setTimeout(animate, delay)
    } else {
      requestAnimationFrame(animate)
    }
  }

  /**
   * Stops the animation.
   */
  function stop() {
    running = false
    window.clearTimeout(delayedStartTimer!)
  }

  /**
   * Animation loop.
   */
  function animate() {
    if (!running) {
      return
    }

    ctx.clearRect(0, 0, canvasWidth, canvasHeight)
    generator.render(ctx)

    requestAnimationFrame(animate)
  }
}

/**
 * Particle generator
 */
function createParticleGenerator() {
  let viewportWidth = 0
  let viewportHeight = 0
  let limit = LIMIT
  let friction = FRICTION
  let gravity = GRAVITY
  let velocityRangeX = VELOCITY_RANGE_X
  let velocityRangeY = VELOCITY_RANGE_Y
  let sizeRange = SIZE_RANGE
  let colorIdx = 0
  const particles: Particle[] = []

  /**
   * Area where confetti can spawn.
   */
  let confettiSource: ConfettiSource

  return {
    configure,
    render,
  }

  /**
   * Configures the generator props.
   *
   * @param {*} props
   */
  function configure(props: ConfettiProps) {
    // Dereferencing like this to avoid having to `props.lolz`.
    limit = get(props, 'limit', limit)
    friction = get(props, 'friction', friction)
    gravity = get(props, 'gravity', gravity)
    viewportHeight = get(props, 'viewportHeight', viewportHeight)
    viewportWidth = get(props, 'viewportWidth', viewportWidth)
    velocityRangeX = get(props, 'velocityRangeX', velocityRangeX)
    velocityRangeY = get(props, 'velocityRangeY', velocityRangeY)
    sizeRange = get(props, 'sizeRange', sizeRange)

    if (!confettiSource) {
      confettiSource = {
        width: viewportWidth,
        height: 0,
        x: 0,
        y: 0,
      }
    }

    confettiSource = {
      ...confettiSource,
      ...props.confettiSource,
    }
  }

  /**
   * Renders all particles and tries to add one.
   *
   * @param {CanvasRenderingContext2D} ctx
   */
  function render(ctx: CanvasRenderingContext2D) {
    maybeAddParticle()

    let len = particles.length
    for (let i = 0; i < len; i++) {
      const particle = particles[i]
      particle.render(ctx)
      if (particle.shouldBeRemoved()) {
        // Since we are removing in-place, need to go back one and also adjust the length-
        particles.splice(i--, 1)
        len--
      }
    }
  }

  /**
   * Adds a particle if there's room.
   */
  function maybeAddParticle() {
    const count = particles.length
    if (count < limit) {
      const newParticleX = random(
        confettiSource.x,
        confettiSource.width + confettiSource.x,
      )
      const newParticleY = random(
        confettiSource.y,
        confettiSource.height + confettiSource.y,
      )

      particles.push(createParticle(newParticleX, newParticleY))
    }
  }

  /**
   * Creates a particle starting at the specified coords.
   *
   * @param {number} x
   * @param {number} y
   */
  function createParticle(x: number, y: number): Particle {
    const color = nextColor()
    const size = random(sizeRange[0], sizeRange[1])
    const force = size * gravity
    const angle = random(0, Math.PI, true)
    const angleStart = angle * Math.PI
    const angleEnd = (angle + PARTICLE_ANGLE) * Math.PI
    const arcX = size / 2
    const arcY = size / 2
    const rotationSpeed = random(
      ROTATION_SPEED_RANGE[0],
      ROTATION_SPEED_RANGE[1],
    )

    let velocityX = random(velocityRangeX[0], velocityRangeX[1])
    let velocityY = random(velocityRangeY[0], velocityRangeY[1])
    let rotationX = 0
    let fadingOut = false
    let opacity = 0.0
    return {
      render,
      shouldBeRemoved,
    }

    /**
     * Updates the particle's state.
     */
    function update() {
      x += velocityX
      y += velocityY
      // Apply force.
      velocityY += force
      // Apply friction
      velocityX *= friction
      velocityY *= friction
      // Apply spin
      rotationX += rotationSpeed

      if (!fadingOut) {
        opacity = Math.min(opacity + FADE_IN_SPEED, 1)
        if (y / viewportHeight > FADE_Y_START) {
          fadingOut = true
        }
      } else {
        opacity = Math.max(opacity - FADE_OUT_SPEED, 0)
      }
    }

    /**
     * Draws the particle.
     *
     * @param {CanvasRenderingContext2D} ctx
     */
    function render(ctx: CanvasRenderingContext2D) {
      // Render the particle.
      if (opacity > 0) {
        ctx.save()
        ctx.translate(x, y)
        ctx.beginPath()
        ctx.rotate(rotationX)
        ctx.globalAlpha = opacity
        ctx.lineWidth = size / 1.25
        ctx.strokeStyle = color
        // I can't even explain why this works. ¯\_(ツ)_/¯
        ctx.arc(arcX, arcY, size, angleStart, angleEnd)
        ctx.stroke()
        ctx.restore()
      }

      // Updates the state
      update()
    }

    /**
     * Determines whether the particle is ready for removal.
     */
    function shouldBeRemoved() {
      return fadingOut && opacity <= 0
    }

    /**
     * Gets the next color to use.
     */
    function nextColor() {
      const color = COLORS[colorIdx]
      colorIdx = COLORS.length === colorIdx + 1 ? 0 : colorIdx + 1
      return color
    }
  }
}

/**
 * Particle
 */
interface Particle {
  render(ctx: CanvasRenderingContext2D): void
  shouldBeRemoved(): boolean
}
